import { loadImageForCanvas } from '@/shared/lib/utils/cors-image-loader';
import { getCanvasRef, getContextRef, resetAllRefs } from '../drawing.refs';
import type { DrawingStateCreator, PersistenceSlice } from './types';

export const createPersistenceSlice: DrawingStateCreator<PersistenceSlice> = (
  set,
  get
) => ({
  session: null,

  exportCanvas: async (format = 'png', quality = 0.9) => {
    const canvasRef = getCanvasRef();
    if (!canvasRef) throw new Error('Canvas not initialized');

    return new Promise((resolve, reject) => {
      canvasRef.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to export canvas'));
          }
        },
        `image/${format}`,
        quality
      );
    });
  },

  importImage: async (file) => {
    const { width, height } = get().canvas;

    const contextRef = getContextRef();
    if (!contextRef) throw new Error('Canvas not initialized');

    set((state) => {
      state.ui.loading.import = true;
      state.ui.error.import = null;
    });

    try {
      const url = URL.createObjectURL(file);
      const img = await loadImageForCanvas(url);
      URL.revokeObjectURL(url);

      // Configure context to prevent pixel degradation during image loading
      contextRef.save();
      contextRef.imageSmoothingEnabled = false; // Disable smoothing to preserve sharp pixels
      contextRef.globalCompositeOperation = 'copy'; // Replace pixels instead of blending
      contextRef.globalAlpha = 1.0; // Full opacity to prevent alpha blending issues

      contextRef.drawImage(img, 0, 0, width, height);

      contextRef.restore();

      // Cleanup blob URL after use
      if (img.src && img.src.startsWith('blob:')) {
        URL.revokeObjectURL(img.src);
      }

      get().saveToHistory('Import');
      get().checkForChanges();

      set((state) => {
        state.ui.loading.import = false;
      });
    } catch (error) {
      set((state) => {
        state.ui.loading.import = false;
        state.ui.error.import =
          error instanceof Error ? error.message : 'Failed to import image';
      });
      throw error;
    }
  },

  saveCanvas: async () => {
    set((state) => {
      state.ui.loading.save = true;
      state.ui.error.save = null;
    });

    try {
      // Render text blocks to canvas before saving
      get().renderTextBlocksToCanvas();

      // Save to history after rendering text (so text is part of the canvas state)
      get().saveToHistory('Render text');

      // Save as PNG to preserve transparency (user edits only, no background)
      const blob = await get().exportCanvas('png');

      set((state) => {
        state.ui.loading.save = false;
        state.history.lastSaveIndex = state.history.currentIndex;
        if (state.session) {
          state.session.savedBlob = blob;
        }
        // Clear text blocks after saving (they are now burned into the canvas)
        state.textBlocks.blocks = [];
        state.textBlocks.activeBlockId = null;
        // Mark as no changes since we just saved
        state.canvas.hasChanges = false;
      });

      return blob;
    } catch (error) {
      set((state) => {
        state.ui.loading.save = false;
        state.ui.error.save =
          error instanceof Error ? error.message : 'Failed to save canvas';
      });
      throw error;
    }
  },

  loadFromBlob: async (blob) => {
    const { width, height } = get().canvas;

    const contextRef = getContextRef();
    if (!contextRef) throw new Error('Canvas not initialized');

    const url = URL.createObjectURL(blob);
    const img = await loadImageForCanvas(url);
    URL.revokeObjectURL(url);

    // Configure context to prevent pixel degradation during image loading
    contextRef.save();
    contextRef.imageSmoothingEnabled = false; // Disable smoothing to preserve sharp pixels
    contextRef.globalCompositeOperation = 'copy'; // Replace pixels instead of blending
    contextRef.globalAlpha = 1.0; // Full opacity to prevent alpha blending issues

    contextRef.drawImage(img, 0, 0, width, height);

    contextRef.restore();

    // Cleanup blob URL after use
    if (img.src && img.src.startsWith('blob:')) {
      URL.revokeObjectURL(img.src);
    }

    get().saveToHistory('Load');
  },

  startSession: (angleImageUrl, initialControlUrl) =>
    set((state) => {
      state.session = {
        id: `drawing-${Date.now()}`,
        startTime: new Date(),
        angleImageUrl,
        initialControlUrl,
        canvasWidth: state.canvas.width,
        canvasHeight: state.canvas.height,
      };
    }),

  endSession: async (save = false) => {
    if (save) {
      await get().saveCanvas();
    }

    set((state) => {
      if (state.session) {
        state.session.endTime = new Date();
      }
    });

    const session = get().session;

    // Clean up angle image blob URL
    const angleImage = get().canvas.angleImage;
    if (angleImage && angleImage.src && angleImage.src.startsWith('blob:')) {
      URL.revokeObjectURL(angleImage.src);
    }

    // Clean up all refs (Map entries and flags)
    resetAllRefs();

    set((state) => {
      state.session = null;
      state.canvas.angleImage = null;
      state.canvas.angleImageUrl = null;
    });

    return session;
  },

  getSessionData: () => get().session,
});
